import {
  CategoryFieldName,
  CategoryName,
  ConditionSetItemsP,
  DomainName,
  ExecutedActionP,
  IAbstractGroupControl,
  IBaseInputMD,
  IBaseInputMDWithRecourse,
  IFormFieldsParsed,
  IFormFragment,
  IFormInputControl,
  IFormParsed,
  IGroupControl,
  ILabelControl,
  ILabeledFormControl,
  IViewFieldWithField,
  resolveButtonedName,
  TFormEditMode,
  TFormName,
  TFormValues,
  tg,
} from '@infotech-soe/metaschema';
import { cloneDeep, defaultsDeep, merge, mergeWith } from 'lodash';

import { TActionArgsValues } from '../../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import CachedSchemaRepository from '../../../infrastructure/schemas/storage/CachedSchemaRepository';
import ISchemaRepository from '../../../infrastructure/schemas/storage/ISchemaRepository';
import DuplicateRecordCommand from '../../commands/wrap/DuplicateRecordCommand';
import ContextService from '../../context/ContextService';
import IContextService from '../../context/IContextService';
import IUUIDService from '../../uid/IUUIDService';
import UUIDService from '../../uid/UUIDService';
import IUserConfigService from '../../user-config/IUserConfigService';
import UserConfigService from '../../user-config/UserConfigService';
import FormApi from '../api/FormApi';
import { FormTitle } from '../FormTitle';
import ConditionFormSchemaFactory from '../schema-factory/ConditionFormSchemaFactory';
import DefaultFormSchemaFactory from '../schema-factory/DefaultFormSchemaFactory';
import {
  IExtendedControl,
  IExtendedFormParsed,
  IReportControl,
} from '../schema-factory/FormSchemaFactory';
import ReviewFormSchemaFactory from '../schema-factory/ReviewFormSchemaFactory';
import TreeFormSchemaFactory from '../schema-factory/TreeFormSchemaFactory';
import { isPredefinedForm } from '../typeguards';
import updateCheckboxGroup from '../updaters/inputs/checkGroup';
import updateDate from '../updaters/inputs/date';
import {
  FormControlViewModelType,
  LocalizedProps,
} from '../view-model/FormControlViewModel';
import FormCreateViewModel, {
  ConditionFormCreateViewModel,
  DefaultFormCreateViewModel,
  PredefinedForms,
  PredefinedTreeFormCreateViewModel,
} from '../view-model/FormCreateViewModel';
import { FormSource, FormSourceType } from '../view-model/FormSource';
import { FormViewModel } from '../view-model/FormViewModel';

import buildInputDecoratedControl from './controls/buildInputDecoratedControl';
import buildLabelControl from './controls/buildLabelControl';
import buildTreeControl from './controls/buildTreeControl';
import { getLocalizedProps } from './controls/helpers/getLocalizedProps';
import { initForm } from './initForm';

const isNotPredefined = (
  x: FormCreateViewModel
): x is DefaultFormCreateViewModel | ConditionFormCreateViewModel =>
  x.type !== FormSourceType.PREDEFINED;

const isLabelControl = (item: any): item is ILabelControl =>
  item?.constructor.name === 'Label';

const isFormFragment = (item: any): item is IFormFragment =>
  item?.constructor.name === 'Fragment';

const isAbstractGroupControl = (item: any): item is IAbstractGroupControl =>
  item?.constructor.name === 'Group';

type FormFieldDomains = Array<{ domain: DomainName; name: CategoryFieldName }>;

class FormBuilder {
  private readonly model: FormViewModel;
  private readonly idService: IUUIDService;
  private readonly contextService: IContextService;
  private readonly schemaRepository: ISchemaRepository;
  private readonly userConfigService: IUserConfigService;
  private formFieldDomains: FormFieldDomains = [];

  constructor(private readonly createModel: FormCreateViewModel) {
    this.idService = new UUIDService();
    this.contextService = new ContextService();
    this.schemaRepository = new CachedSchemaRepository();
    this.userConfigService = new UserConfigService();
    this.model = {
      id:
        (this.createModel as PredefinedTreeFormCreateViewModel).formId ||
        this.idService.generateId(),
      title: this.getFormTitle(),
      readonly: false,
      controls: {},
      controlsOrder: [],
      args: {},
      rootControl: '',
      updater: {},
      utils: {},
      skipNextUpdater: false,
      apiVars: new Map<string, any>().set('formCategory', createModel.category),
      submitting: false,
      hasHints: false,
      areHintsShown: false,
      source: {} as FormSource,
    };
  }

  private static readonly predefinedFormFactory = {
    [PredefinedForms.TREE]: new TreeFormSchemaFactory(),
    [PredefinedForms.REVIEW]: new ReviewFormSchemaFactory(),
  } as const;

  private async parseForm(): Promise<[IFormParsed, FormSource]> {
    const schemas: IExtendedFormParsed[] = [];
    let mainSource: FormSource | undefined = undefined;

    const addSchema = async (
      model: FormCreateViewModel,
      mergedForm?: TFormName
    ) => {
      if (tg.isString(mergedForm) && isNotPredefined(model)) {
        const { category, name } = resolveButtonedName(mergedForm);
        model.category = category ?? model.category;
        model.formName = name;
      }
      const source = await this.getFormSource(model);
      if (tg.isUndefined(mergedForm)) {
        mainSource = source;
      }
      const formSchema: IExtendedFormParsed = await this.getFormSchema(
        model,
        source
      );
      schemas.push(cloneDeep(formSchema));
    };

    await addSchema({ ...this.createModel });
    if (!(schemas[0] && mainSource !== undefined)) {
      throw new Error(`Cannot parse form`);
    }
    let result = {};
    const customizer = (objV: any, srcV: any, key: string): any => {
      if (
        (key === 'Layout' || key === 'children') &&
        Array.isArray(objV) &&
        Array.isArray(srcV)
      ) {
        const items = [];
        for (const item of objV) {
          const srcItem = srcV.find(i => i.name === item.name);
          if (srcItem) {
            items.push(mergeWith(item, srcItem, customizer));
          } else {
            items.push(item);
          }
        }
        for (const item of srcV) {
          if (!objV.some(i => i.name === item.name)) {
            items.push(item);
          }
        }
        return items;
      } else if (key === 'Clear' && srcV) {
        return srcV;
      }
    };
    for (const element of schemas) {
      result = mergeWith(result, element, customizer);
    }
    return [result as IFormParsed, mainSource];
  }

  public async build(): Promise<FormViewModel> {
    const [formSchema, source] = await this.parseForm();

    if (formSchema.Layout.length === 0) {
      throw new Error(
        `Invalid Form(${this.createModel.type}): Layout: is empty`
      );
    }
    if (formSchema.Layout.length > 1) {
      throw new Error(
        `Invalid Form(${this.createModel.type}): Layout: must have only one root group control`
      );
    }

    let actionArgs: TActionArgsValues = {};
    if (!isPredefinedForm(this.createModel)) {
      actionArgs = { ...this.createModel.args };
    }

    this.model.source = source;
    this.model.args = actionArgs;
    this.model.updater = formSchema.Updater;
    if (formSchema.Utils) {
      this.model.utils = formSchema.Utils;
    }
    if (formSchema.Clear) {
      this.model.clear = formSchema.Clear;
    }
    if (this.createModel.type === FormSourceType.PREDEFINED) {
      this.model.width = this.createModel.width;
    } else if (formSchema.Style?.width) {
      this.model.width = formSchema.Style.width;
    }
    if (formSchema.Style?.height) {
      this.model.height = formSchema.Style.height;
    }

    this.formFieldDomains = await this.getFieldDomains(
      formSchema,
      this.createModel,
      source.form
    );

    for (const item of formSchema.Layout) {
      // TODO: prevent mutation of input args - return controls
      await this.addFormControl(
        item,
        false,
        (this.createModel as PredefinedTreeFormCreateViewModel).values
      );
    }

    for (const [name, { resId, updater, disabled }] of Object.entries(
      formSchema.Buttons || {}
    )) {
      const { label } = await getLocalizedProps(this.model, { name, resId });
      this.model.controls[name] = {
        type: FormControlViewModelType.FORM_BUTTON,
        hasHistory: false,
        hasValue: false,
        name,
        label,
        updater,
        disabled,
      };
    }

    if (formSchema.Init && !isPredefinedForm(this.createModel)) {
      const formApi = new FormApi(
        this.model.apiVars,
        this.createModel.type === FormSourceType.FORM &&
          this.createModel.multiStep
          ? this.createModel.multiStep.prevState || {}
          : {},
        this.createModel.application
      );

      const initResult = await initForm(
        await this.contextService.getContext(
          this.createModel.categoryState,
          this.createModel.datasetsState,
          this.createModel.dataset
        ),
        actionArgs,
        source,
        formSchema.Init,
        formApi,
        formSchema.Utils || {}
      );

      if (initResult) {
        for (const [key, val] of Object.entries(initResult)) {
          const control = this.model.controls[key];

          if ('value' in val) {
            if (control?.type === FormControlViewModelType.CHECKBOX_GROUP) {
              actionArgs[key] = updateCheckboxGroup(control, val.value);
              control.value = val.value;
            } else if (
              control?.type === FormControlViewModelType.DATE ||
              control?.type === FormControlViewModelType.DICT_DATE
            ) {
              actionArgs[key] = updateDate(val.value, control);
              val.value = actionArgs[key];
            } else {
              actionArgs[key] = val.value;
            }
          }

          if (val.streamId) {
            const apiProvider = new MerpsApiProvider();
            const buf = await apiProvider.streams.read(val.streamId);
            if (control.type === FormControlViewModelType.RICHTEXT) {
              val.value = Buffer.from(buf).toString('utf-8');
            } else {
              val.value = buf;
            }
          }

          if (control && control.type !== FormControlViewModelType.TREE) {
            this.model.controls[key] = defaultsDeep({}, val, control);
          }
        }
        this.model.apiVars = formApi.getVars();
      }
    }

    return this.model;
  }

  private async getFormSource(
    createModel: FormCreateViewModel
  ): Promise<FormSource> {
    const getEditMode = (
      actionType: string,
      model: DefaultFormCreateViewModel
    ): TFormEditMode => {
      if (actionType === 'Update') {
        return 'edit';
      } else if (actionType === 'Create') {
        if (model.command === DuplicateRecordCommand.COMMAND_NAME) {
          return 'dub';
        } else {
          return 'add';
        }
      } else {
        return 'other';
      }
    };
    switch (createModel.type) {
      case FormSourceType.CONDITION:
        return {
          type: FormSourceType.CONDITION,
          category: createModel.category,
          form: createModel.formName,
          categoryId: createModel.categoryState.id,
          dataset: createModel.dataset,
          conditions: createModel.conditions,
          formConfig: await this.userConfigService.getFormConfig(
            createModel.category,
            createModel.formName
          ),
          application: createModel.application,
          hook: createModel.hook,
          editMode: 'other',
        };
      case FormSourceType.PREDEFINED:
        if (createModel.subtype === PredefinedForms.REVIEW) {
          return {
            action: createModel.subtype,
            caption: '',
            category: createModel.category,
            dataset: createModel.dataset,
            form: '',
            template: createModel.template,
            treeId: '',
            type: createModel.type,
          };
        }
        return {
          type: FormSourceType.PREDEFINED,
          action: createModel.subtype,
          form: '',
          category: createModel.category,
          treeId: createModel.treeId,
          caption: createModel.caption,
        };
      default: {
        const actionSchema = (await this.schemaRepository.getAction(
          createModel.category,
          createModel.action
        )) as ExecutedActionP;

        if (tg.isActionWithForm(actionSchema)) {
          return {
            type: createModel.type,
            category: createModel.category,
            form: actionSchema.Form,
            categoryId: createModel.categoryState.id,
            dataset: createModel.dataset,
            action: createModel.action,
            formConfig: await this.userConfigService.getFormConfig(
              createModel.category,
              actionSchema.Form
            ),
            multiStep: createModel.multiStep,
            application: createModel.application,
            hook: createModel.hook,
            editMode: getEditMode(actionSchema.Type, createModel),
          };
        } else {
          throw new Error(
            `Action ${createModel.action} in category: ${createModel.category} hasn't form`
          );
        }
      }
    }
  }

  private getFormTitle(): FormTitle {
    if (!isPredefinedForm(this.createModel)) {
      if (this.createModel.title) {
        return {
          [this.createModel.categoryState.datasets[this.createModel.dataset]
            .title]: this.createModel.title,
        };
      }
    } else if (this.createModel.type === FormSourceType.PREDEFINED) {
      const factory =
        FormBuilder.predefinedFormFactory[this.createModel.subtype];
      if (factory) {
        return this.createModel.caption || factory.getTitle();
      }
    }

    return '';
  }

  private async getFormSchema(
    createModel: FormCreateViewModel,
    source: FormSource
  ): Promise<IExtendedFormParsed> {
    if (createModel.type === FormSourceType.PREDEFINED) {
      if (createModel.subtype === PredefinedForms.REVIEW) {
        return FormBuilder.predefinedFormFactory[
          createModel.subtype
        ].getFormSchema(
          createModel.template,
          createModel.values,
          createModel.variables
        );
      } else {
        return FormBuilder.predefinedFormFactory[
          createModel.subtype
        ].getFormSchema();
      }
    }
    if (createModel.type === FormSourceType.CONDITION) {
      return new ConditionFormSchemaFactory().get(source);
    } else if (
      createModel.type === FormSourceType.FORM ||
      createModel.type === FormSourceType.PRINT
    ) {
      return new DefaultFormSchemaFactory().get(source);
    }

    throw new Error(`undefined form type ${createModel.type}`);
  }

  private async getFieldDomains(
    formSchema: IFormParsed,
    createModel: FormCreateViewModel,
    formName: TFormName
  ): Promise<Array<{ domain: DomainName; name: CategoryFieldName }>> {
    if (createModel.type === FormSourceType.PREDEFINED) {
      // TODO: get id domain by id field of schema
      // const domain = await this.schemaRepository.getDomain('Id');

      return [];
    }

    return this.getFormFieldsDomains(
      formSchema.Fields,
      createModel.category,
      formName,
      (createModel as ConditionFormCreateViewModel)?.conditions
    );
  }

  private async getFormFieldsDomains(
    fields: IFormFieldsParsed,
    category: CategoryName,
    formName: TFormName,
    actionDomains: ConditionSetItemsP = {}
  ): Promise<FormFieldDomains> {
    if (fields === undefined) {
      return [];
    }

    const domains: FormFieldDomains = Object.entries(actionDomains).map(
      ([name, domainDef]) => ({
        name,
        domain: typeof domainDef === 'string' ? domainDef : domainDef.domain,
      })
    );
    const resolved = await this.schemaRepository.getFormFieldsDomain(
      category,
      formName
    );

    for (const [name, val] of Object.entries(fields)) {
      const resolvedField = { name, domain: '' };

      // eslint-disable-next-line no-prototype-builtins
      if (resolved.hasOwnProperty(name)) {
        resolvedField.domain = resolved[name];
        domains.push(resolvedField);
      } else if (tg.hasDomainProp(val)) {
        resolvedField.domain = val.domain;
        domains.push(resolvedField);
      } else if (tg.hasCategoryFieldPath(val)) {
        const field = await this.schemaRepository.getField(
          category,
          (val as IViewFieldWithField).field
        );

        if (field.domain) {
          resolvedField.domain = field.domain;
          domains.push(resolvedField);
        } else {
          throw new Error('Incorrect schema.');
        }
      }
    }

    return domains;
  }

  private async injectFormFragment(
    itemName: string,
    formName: TFormName,
    withoutRoutines: boolean
  ) {
    const formSchema = await this.schemaRepository.getForm(
      this.createModel.category,
      formName
    );
    if (formSchema.Extends) {
      throw new Error(
        `Form fragment "${formName}" should not have "Extends" properties`
      );
    }
    const fieldDomains = await this.getFormFieldsDomains(
      formSchema.Fields,
      this.createModel.category,
      formName,
      (this.createModel as ConditionFormCreateViewModel)?.conditions
    );
    for (const { name, domain } of fieldDomains) {
      if (!this.formFieldDomains.some(d => d.name === name)) {
        this.formFieldDomains.push({ name, domain });
      }
    }
    if (!withoutRoutines) {
      if (formSchema.Utils) {
        merge(this.model.utils, formSchema.Utils);
      }
      if (formSchema.Updater) {
        merge(this.model.updater, formSchema.Updater);
      }
    }

    await this.addBox(
      {
        borderless: true,
        control: 'GroupBox',
        name: itemName,
        noLabel: true,
        children: formSchema.Layout,
      },
      { label: itemName }
    );
  }

  private async handleGroup(
    item: IAbstractGroupControl,
    label: string,
    hint?: string
  ) {
    switch (item.control) {
      case 'Tabs': {
        const children: string[] = [];

        if (item.children) {
          for (const childTab of item.children) {
            children.push(
              ...((await this.addFormControl(childTab, true)) ?? [])
            );
          }
        }

        this.model.controls[item.name] = {
          noLabel: (item as IBaseInputMDWithRecourse).noLabel,
          type: FormControlViewModelType.TABS,
          name: item.name,
          rootControl: this.model.rootControl,
          hasValue: false,
          hasHistory: false,
          label,
          children,
        };
        break;
      }
      case 'Tab': {
        const paneChildren: string[] = [];

        if (item.children) {
          for (const paneChild of item.children) {
            paneChildren.push(
              ...((await this.addFormControl(paneChild, true)) ?? [])
            );
          }
        }
        this.model.controls[item.name] = {
          noLabel: (item as IBaseInputMDWithRecourse).noLabel,
          type: FormControlViewModelType.TABS_PANE,
          fullHeightGroup: item.fullHeightGroup,
          name: item.name,
          label,
          children: paneChildren,
          hasValue: false,
          hasHistory: false,
          visible: (item as IBaseInputMD).visible ?? true,
        };
        break;
      }
      default:
        await this.addBox(item as IGroupControl<'GroupBox'>, { label, hint });
    }
  }

  private async addFormControl(
    item: IExtendedControl,
    returnCreatedControlId?: boolean,
    values?: TFormValues
  ): Promise<void | string[]> {
    const foundItem = Object.values(this.model.controls).find(
      c => c.name === item.name
    );
    if (foundItem) {
      return returnCreatedControlId ? undefined : [foundItem.name];
    }

    if (this.model.rootControl === '') {
      this.model.rootControl = item.name;
    }

    const { label, hint } = await getLocalizedProps(this.model, item);
    this.model.hasHints ||= !!hint;

    if ('control' in item && item.control === 'Tree') {
      const tree = (this.model.controls[item.name] = await buildTreeControl(
        item,
        this.model,
        label
      ));

      if (values) {
        tree.value = values[item.name] || null;
      }
    } else if (item.constructor.name === 'Input') {
      const input = (this.model.controls[item.name] =
        await buildInputDecoratedControl(
          this.model,
          item as IFormInputControl,
          { label, hint },
          this.model.args,
          this.formFieldDomains
        ));

      if (
        values &&
        input.type !== FormControlViewModelType.BUTTON &&
        input.type !== FormControlViewModelType.DICT_BUTTON &&
        input.type !== FormControlViewModelType.SIGNATURE_BUTTON
      ) {
        input.value = values[item.name] || null;
      }
    } else if (isLabelControl(item)) {
      this.model.controls[item.name] = await buildLabelControl(
        item,
        { label, hint },
        this.model.args
      );
    } else if (isAbstractGroupControl(item)) {
      await this.handleGroup(item, label, hint);
    } else if (
      this.model.source.type === FormSourceType.PREDEFINED &&
      this.model.source.action === PredefinedForms.REVIEW &&
      'control' in item &&
      item.control === FormControlViewModelType.REPORT
    ) {
      this.addReport(item);
    } else if (isFormFragment(item)) {
      await this.injectFormFragment(
        item.name,
        item.form,
        item.withoutRoutines ?? false
      );
    } else {
      throw new Error(
        `Form decorator "${item.constructor.name}" isn't implemented yet`
      );
    }

    if (returnCreatedControlId) {
      return [item.name];
    }
  }

  private async addBox(
    item: IGroupControl<'GroupBox'>,
    localizedProps: LocalizedProps
  ): Promise<void> {
    const defChildren: string[] = [];
    if (item.children) {
      for (const child of item.children) {
        defChildren.push(...((await this.addFormControl(child, true)) ?? []));
      }
    }

    const { formConfig }: Record<string, any> = this.model.source;
    const collapsed =
      item.collapsible &&
      (formConfig?.[item.name]?.collapsed ?? item.defaultCollapsed);

    this.model.controls[item.name] = {
      columns: (item as IBaseInputMD).columns,
      noLabel: (item as IBaseInputMDWithRecourse).noLabel,
      type: FormControlViewModelType.BOX,
      name: item.name,
      children: defChildren,
      hasValue: false,
      hasHistory: false,
      visible: (item as IBaseInputMD).visible ?? true,
      disabled: (item as IBaseInputMD).disabled || undefined,
      borderless: item.borderless || undefined,
      collapsible: item.collapsible || undefined,
      fullHeightGroup: item.fullHeightGroup,
      collapsed,
      ...localizedProps,
    };

    if ((item as IBaseInputMDWithRecourse).noLabel) {
      this.model.controls[item.name].label = '';
    } else {
      this.model.controls[item.name].labelWidth = (
        item as ILabeledFormControl
      ).labelWidth;
    }
  }

  private addReport(item: IReportControl): void {
    this.model.controls[item.name] = {
      hasHistory: true,
      hasValue: true,
      label: item.name,
      name: item.name,
      template: item.template,
      type: FormControlViewModelType.REPORT,
      value: item.value,
      variables: item.variables,
    };
  }
}

export default FormBuilder;
